import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    Grid,
    TextField,
    Typography,
    Container,
    IconButton,
    Box,
    InputAdornment,
    Alert,
    Fade,
} from '@mui/material'
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    clearMessageError,
    login
} from '../../redux/actions/authAction'
import CustomButton from '../../components/CustomButton'
import LanguageSelector from '../../components/LanguageSelector'
import { isMobile } from '../../utils/utils'

const GOLD = '#c9a96e'

const fadeInUpKeyframes = {
    '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(24px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
}

const stagger = (index) => ({
    opacity: 0,
    animation: 'fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    animationDelay: `${index * 0.1}s`,
})

const inputSx = {
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
        color: '#fff',
        transition: 'all 0.2s ease',
        '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.08)',
            transition: 'border-color 0.2s ease',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.16)',
        },
        '&.Mui-focused fieldset': {
            borderColor: GOLD,
            borderWidth: '1.5px',
        },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px #111111 inset',
            WebkitTextFillColor: '#fff',
            caretColor: '#fff',
            borderRadius: '12px',
            transition: 'background-color 5000s ease-in-out 0s',
        },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"DM Sans", sans-serif',
        color: 'rgba(255, 255, 255, 0.35)',
        fontSize: '14px',
        '&.Mui-focused': {
            color: GOLD,
        },
    },
    '& .MuiFormHelperText-root': {
        fontFamily: '"DM Sans", sans-serif',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: 'rgba(255, 255, 255, 0.25)',
        fontSize: '20px',
    },
}

const LoginScreen = () => {
    const dispatch = useDispatch()
    const { loading, error, user } = useSelector(state => state.auth)
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [emailError, setEmailError] = useState(false)
    const [passwordError, setPasswordError] = useState('')
    const isMobileView = isMobile()

    const handleLogin = async () => {
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        setEmailError(!emailValid)

        const isValidPassword = password !== ""
        if (!isValidPassword) {
            setPasswordError(!isValidPassword)
            return
        } else {
            setPasswordError('')
        }

        if (emailValid) {
            await dispatch(login(email, password, navigate))
        }
    }

    const handleForgotPassword = () => {
        navigate('/forgot-password')
    }

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleLogin()
        }
    }

    useEffect(() => {
        if (user) {
            navigate('/')
        }
    }, [user, navigate])

    useEffect(() => {
        dispatch(clearMessageError())
    }, [dispatch])

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#000',
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                ...fadeInUpKeyframes,
            }}
        >
            {/* Grain texture overlay */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.025,
                    pointerEvents: 'none',
                    zIndex: 1,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: '256px 256px',
                }}
            />

            {/* Language selector */}
            <Box sx={{ position: 'absolute', top: 20, right: 24, zIndex: 10 }}>
                <LanguageSelector variant="dark" />
            </Box>

            <Grid container sx={{ minHeight: '100vh', position: 'relative', zIndex: 2 }}>
                {/* Left Column — Branding */}
                {!isMobileView && (
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            background: '#000',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            padding: 6,
                        }}
                    >
                        {/* Radial gold glow */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: `radial-gradient(ellipse 70% 50% at 50% 40%, rgba(201, 169, 110, 0.05) 0%, transparent 70%)`,
                                pointerEvents: 'none',
                            }}
                        />

                        {/* Subtle grid overlay */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.025,
                                backgroundImage:
                                    'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                                backgroundSize: '80px 80px',
                                pointerEvents: 'none',
                            }}
                        />

                        {/* Logo */}
                        <Box
                            sx={{
                                ...stagger(0),
                                mb: 5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 100,
                                    height: 100,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    backgroundColor: 'rgba(255,255,255,0.02)',
                                    overflow: 'hidden',
                                }}
                            >
                                <img
                                    src="/logo/logo-circle-dark-mode.svg"
                                    alt="Lawis Logo"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        padding: '10px',
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Heading */}
                        <Typography
                            sx={{
                                ...stagger(1),
                                fontFamily: '"Cormorant Garamond", Georgia, serif',
                                fontWeight: 300,
                                fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
                                color: '#fff',
                                textAlign: 'center',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.1,
                                mb: 2.5,
                                zIndex: 2,
                                position: 'relative',
                            }}
                        >
                            {t('auth_welcome_title')}
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                            sx={{
                                ...stagger(2),
                                fontFamily: '"DM Sans", sans-serif',
                                fontWeight: 400,
                                fontSize: '15px',
                                color: 'rgba(255, 255, 255, 0.40)',
                                textAlign: 'center',
                                maxWidth: '380px',
                                lineHeight: 1.7,
                                zIndex: 2,
                                position: 'relative',
                            }}
                        >
                            {t('auth_welcome_subtitle')}
                        </Typography>

                        {/* Decorative gold line */}
                        <Box
                            sx={{
                                ...stagger(3),
                                width: '48px',
                                height: '1.5px',
                                backgroundColor: GOLD,
                                opacity: 0.4,
                                mt: 4,
                                borderRadius: '1px',
                            }}
                        />
                    </Grid>
                )}

                {/* Right Column — Login Form */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        padding: isMobileView ? 3 : 6,
                        backgroundColor: '#0d0d0d',
                        borderLeft: isMobileView ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
                        position: 'relative',
                    }}
                >
                    {/* Mobile logo */}
                    {isMobileView && (
                        <Box sx={{ ...stagger(0), mb: 4, mt: 4 }}>
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    backgroundColor: 'rgba(255,255,255,0.02)',
                                    overflow: 'hidden',
                                }}
                            >
                                <img
                                    src="/logo/logo-circle-dark-mode.svg"
                                    alt="Lawis Logo"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        padding: '8px',
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    <Container maxWidth="sm" sx={{ maxWidth: '420px !important' }}>
                        {/* Section label */}
                        <Typography
                            sx={{
                                ...stagger(0),
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '11px',
                                fontWeight: 600,
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                color: 'rgba(255, 255, 255, 0.25)',
                                mb: 2,
                            }}
                        >
                            {t('auth_login_title')}
                        </Typography>

                        {/* Main heading */}
                        <Typography
                            sx={{
                                ...stagger(1),
                                fontFamily: '"Cormorant Garamond", Georgia, serif',
                                fontWeight: 300,
                                fontSize: isMobileView ? '2rem' : '2.6rem',
                                color: '#fff',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.1,
                                mb: 1,
                            }}
                        >
                            {t('auth_login_subtitle')}
                        </Typography>

                        {/* Decorative line */}
                        <Box
                            sx={{
                                ...stagger(2),
                                width: '32px',
                                height: '1.5px',
                                backgroundColor: GOLD,
                                opacity: 0.5,
                                mb: 4,
                                mt: 2,
                                borderRadius: '1px',
                            }}
                        />

                        {/* Error alert */}
                        {error && (
                            <Fade in={!!error}>
                                <Alert
                                    severity="error"
                                    sx={{
                                        mb: 3,
                                        borderRadius: '12px',
                                        fontFamily: '"DM Sans", sans-serif',
                                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                        color: '#fca5a5',
                                        border: '1px solid rgba(239, 68, 68, 0.15)',
                                        '& .MuiAlert-icon': {
                                            color: '#f87171',
                                        },
                                    }}
                                >
                                    {error}
                                </Alert>
                            </Fade>
                        )}

                        {/* Email field */}
                        <Box sx={stagger(3)}>
                            <TextField
                                required
                                fullWidth
                                label={t('auth_email_label')}
                                margin="none"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                error={emailError}
                                helperText={emailError ? t('auth_email_invalid') : ''}
                                onKeyPress={handleKeyPress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={inputSx}
                            />
                        </Box>

                        {/* Password field */}
                        <Box sx={stagger(4)}>
                            <TextField
                                required
                                fullWidth
                                label={t('auth_password_label')}
                                margin="none"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                error={!!passwordError}
                                helperText={passwordError}
                                onKeyPress={handleKeyPress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                sx={{ color: 'rgba(255,255,255,0.25)' }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={inputSx}
                            />
                        </Box>

                        {/* Forgot password */}
                        <Box sx={{ ...stagger(5), display: 'flex', justifyContent: 'flex-end', mb: 3.5 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    cursor: 'pointer',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '13px',
                                    color: GOLD,
                                    fontWeight: 500,
                                    opacity: 0.7,
                                    transition: 'opacity 0.2s ease',
                                    '&:hover': {
                                        opacity: 1,
                                    },
                                }}
                                onClick={handleForgotPassword}
                            >
                                {t('auth_forgot_password')}
                            </Typography>
                        </Box>

                        {/* Login button */}
                        <Box sx={stagger(6)}>
                            <CustomButton
                                text={t('auth_login_button')}
                                outlined={false}
                                loading={loading}
                                onClick={handleLogin}
                                sx={{
                                    width: '100%',
                                    py: 1.6,
                                    borderRadius: '12px',
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    letterSpacing: '0.02em',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    transition: 'all 0.2s ease',
                                    mb: 4,
                                    ':hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.88)',
                                        boxShadow: 'none',
                                    },
                                }}
                            />
                        </Box>

                        {/* Divider */}
                        <Box
                            sx={{
                                ...stagger(7),
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4,
                            }}
                        >
                            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
                            <Typography
                                sx={{
                                    px: 2.5,
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '12px',
                                    color: 'rgba(255, 255, 255, 0.2)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                {t('auth_or')}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
                        </Box>

                        {/* Subscribe link */}
                        <Box sx={{ ...stagger(8), textAlign: 'center' }}>
                            <Typography
                                sx={{
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '14px',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                }}
                            >
                                {t('auth_no_account')}{' '}
                                <Typography
                                    component="span"
                                    onClick={() => {
                                        window.location.href = "https://www.lawis.ai/#pricing"
                                    }}
                                    sx={{
                                        fontFamily: '"DM Sans", sans-serif',
                                        fontSize: '14px',
                                        color: GOLD,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s ease',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                    }}
                                >
                                    {t('auth_signup_link')}
                                </Typography>
                            </Typography>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
        </Box>
    )
}

export default LoginScreen
